import { Link, useMatch } from 'react-router-dom';
import { SITE } from '../data/content/site';

/**
 * StaticFooter Component
 *
 * Reusable footer for logged-out pages (HomePage, WaitlistPage)
 * Contains privacy links and copyright - part of page, not app layout
 *
 * Link order follows Frank's IA feedback (#803):
 * About | For Leaders | For Members | FAQ | Contact | Privacy | Terms
 * (grouped by purpose: product info → support → legal)
 *
 * "For Leaders" → the leaders argument page (/leaders, LeaderWhyPage #3125),
 * which itself hands off to the Getting Started guide (#2836). Argument before
 * instructions — the how-to is a click deeper, not the entry point (#3222).
 *
 * "For Members" → the members argument page (/members, MembersPage #3218), the
 * participant's counterpart to "For Leaders". Added to the Product Info group
 * beside it per owner decision 2026-09-10, matching the #803 order convention.
 */
export default function StaticFooter() {
  // #3126 r1 (George M2): hide the "Get the iPhone app" affordance on /app
  // itself, where it would be a self-link. The App Store badge in the page
  // close stays the primary action.
  // #3126 r2 (Frank Low): useMatch('/app') also matches the trailing-slash
  // variant `/app/` (served by the SPA catch-all), which an exact
  // `pathname === '/app'` compare would leak the self-link back on.
  const onAppPage = useMatch('/app') !== null;
  const handleLinkClick = () => {
    // Scroll to top when navigating to legal/info pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full flex flex-col items-center gap-6 py-6 pb-12 px-4">
      {/* Footer Links - ordered by purpose (#803) */}
      <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs">
        {/* Product Info */}
        <Link
          to="/about"
          onClick={handleLinkClick}
          className="text-text-secondary hover:text-accent transition-colors min-h-11 flex items-center"
        >
          About
        </Link>
        <Link
          to="/leaders"
          onClick={handleLinkClick}
          className="text-text-secondary hover:text-accent transition-colors min-h-11 flex items-center"
        >
          For Leaders
        </Link>
        <Link
          to="/members"
          onClick={handleLinkClick}
          className="text-text-secondary hover:text-accent transition-colors min-h-11 flex items-center"
        >
          For Members
        </Link>
        {/* Support */}
        <Link
          to="/faq"
          onClick={handleLinkClick}
          className="text-text-secondary hover:text-accent transition-colors min-h-11 flex items-center"
        >
          FAQ
        </Link>
        <Link
          to="/contact"
          onClick={handleLinkClick}
          className="text-text-secondary hover:text-accent transition-colors min-h-11 flex items-center"
        >
          Contact
        </Link>
        {/* Legal */}
        <Link
          to="/privacy"
          onClick={handleLinkClick}
          className="text-text-secondary hover:text-accent transition-colors min-h-11 flex items-center"
        >
          Privacy
        </Link>
        <Link
          to="/terms"
          onClick={handleLinkClick}
          className="text-text-secondary hover:text-accent transition-colors min-h-11 flex items-center"
        >
          Terms
        </Link>
        {/* #3126: the App Store affordance every public page carries, beside the
            site links. Now a react-router <Link> to the /app landing page (a
            real client route); the store 302 (/ios) stays on the badge + FAQ.
            This is the footer's app entry, so /app is NOT also added to the
            product-info nav above. Hidden on /app itself (George M2), where it
            would be a self-link. */}
        {!onAppPage && (
          <Link
            to="/app"
            onClick={handleLinkClick}
            className="text-text-secondary hover:text-accent transition-colors min-h-11 flex items-center"
          >
            Get the iPhone app
          </Link>
        )}
      </nav>

      {/* Concrete trust claims (#3123, owner decision 2026-08-23). Here rather
          than on the front door alone so every public page carries them. Each
          claim is pinned to the wording that backs it — privacy.ts for ads and
          selling, terms.ts for passwords — by StaticFooter.test.tsx. See
          data/content/site.ts, which also keeps the string inside
          check-voice.mjs's roots. */}
      <div className="flex flex-col items-center gap-1 text-center text-xs text-text-muted">
        {/* The free offer (#3512), echoed from the front door. Its own line so
            the trust line keeps its pinned wording; backing in site.ts. */}
        <p>{SITE.footerOffer}</p>
        <p>{SITE.footerTrust}</p>
      </div>

      {/* Copyright - below links to avoid iOS URL bar overlap */}
      <p className="text-xs text-text-muted">
        © {currentYear} ORO, LLC. All rights reserved.
      </p>
    </footer>
  );
}